(function (global) {
  'use strict';

  var MINIMUM_NOTICE_MS = 24 * 60 * 60 * 1000;
  var INPUT_STEP_MS = 15 * 60 * 1000;
  var DEFAULT_TIME_ZONE = 'America/Los_Angeles';

  function partsInTimeZone(date, timeZone) {
    var formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hourCycle: 'h23'
    });
    var result = {};

    formatter.formatToParts(date).forEach(function (part) {
      if (part.type !== 'literal') result[part.type] = part.value;
    });

    return result;
  }

  function formatForDateTimeInput(date, timeZone) {
    var parts = partsInTimeZone(date, timeZone || DEFAULT_TIME_ZONE);
    return parts.year + '-' + parts.month + '-' + parts.day + 'T' + parts.hour + ':' + parts.minute;
  }

  function offsetAt(date, timeZone) {
    var parts = partsInTimeZone(date, timeZone);
    var representedAsUtc = Date.UTC(
      Number(parts.year),
      Number(parts.month) - 1,
      Number(parts.day),
      Number(parts.hour),
      Number(parts.minute),
      Number(parts.second)
    );

    return representedAsUtc - Math.floor(date.getTime() / 1000) * 1000;
  }

  function zonedDateTimeToDate(value, timeZone) {
    var match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/.exec(value || '');
    if (!match) return null;

    var wallTimeAsUtc = Date.UTC(
      Number(match[1]),
      Number(match[2]) - 1,
      Number(match[3]),
      Number(match[4]),
      Number(match[5]),
      0
    );
    var firstProbe = new Date(wallTimeAsUtc);
    var firstOffset = offsetAt(firstProbe, timeZone);
    var result = new Date(wallTimeAsUtc - firstOffset);
    var correctedOffset = offsetAt(result, timeZone);

    if (correctedOffset !== firstOffset) {
      result = new Date(wallTimeAsUtc - correctedOffset);
    }

    // Reject impossible local times during the spring DST transition.
    if (formatForDateTimeInput(result, timeZone) !== value) return null;
    return result;
  }

  function minimumCarryDate(now) {
    var earliest = now.getTime() + MINIMUM_NOTICE_MS;
    return new Date(Math.ceil(earliest / INPUT_STEP_MS) * INPUT_STEP_MS);
  }

  function noticeIsValid(value, now, timeZone) {
    var selected = zonedDateTimeToDate(value, timeZone || DEFAULT_TIME_ZONE);
    return Boolean(selected && selected.getTime() >= now.getTime() + MINIMUM_NOTICE_MS);
  }

  function displayCarryDate(value, timeZone) {
    var selected = zonedDateTimeToDate(value, timeZone);
    if (!selected) return '';

    return new Intl.DateTimeFormat('en-US', {
      timeZone: timeZone,
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      timeZoneName: 'short'
    }).format(selected);
  }

  function checkedValue(form, name) {
    var selected = form.querySelector('input[name="' + name + '"]:checked');
    return selected ? selected.value : '';
  }

  function setSummary(root, key, value, fallback) {
    var target = root.querySelector('[data-fp-summary="' + key + '"]');
    if (target) target.textContent = value || fallback;
  }

  function setErrorBox(errorBox, errors) {
    if (!errorBox) return;
    errorBox.replaceChildren();

    if (!errors.length) {
      errorBox.hidden = true;
      return;
    }

    var heading = document.createElement('p');
    heading.textContent = errors.length === 1 ? 'Please finish this detail:' : 'Please finish these details:';
    heading.style.margin = '0 0 0.35rem';
    errorBox.appendChild(heading);

    var list = document.createElement('ul');
    list.style.margin = '0';
    list.style.paddingLeft = '1.2rem';
    errors.forEach(function (error) {
      var item = document.createElement('li');
      item.textContent = error.message;
      list.appendChild(item);
    });
    errorBox.appendChild(list);
    errorBox.hidden = false;
  }

  function captureHeroState(root) {
    var image = root && root.querySelector('.fp-commission__hero-image');
    var caption = root && root.querySelector('[data-fp-hero-caption]');
    if (!image) return null;

    return {
      src: image.getAttribute('src') || '',
      srcset: image.getAttribute('srcset') || '',
      alt: image.getAttribute('alt') || '',
      caption: caption ? caption.textContent : ''
    };
  }

  function updateBaseHero(root, base, defaultState) {
    var image = root && root.querySelector('.fp-commission__hero-image');
    var caption = root && root.querySelector('[data-fp-hero-caption]');
    if (!image || !defaultState) return false;

    var baseKeys = { White: 'white', Red: 'red', Pink: 'pink' };
    var baseKey = baseKeys[base] || '';
    var nextState = defaultState;

    if (baseKey) {
      var trigger = root.querySelector('[data-fp-base-preview="' + baseKey + '"]');
      var proofImage = trigger && trigger.querySelector('.fp-commission__proof-image');

      if (trigger && proofImage) {
        var candidateSrc = trigger.dataset.fpProofSrc || proofImage.getAttribute('src') || '';
        if (candidateSrc) {
          nextState = {
            src: candidateSrc,
            srcset: proofImage.getAttribute('srcset') || '',
            alt: trigger.dataset.fpProofAlt || proofImage.getAttribute('alt') || '',
            caption: 'Shown: ' + base + ' base example.'
          };
        }
      }
    }

    if (nextState.srcset && image.getAttribute('srcset') !== nextState.srcset) {
      image.setAttribute('srcset', nextState.srcset);
    } else if (!nextState.srcset && image.hasAttribute('srcset')) {
      image.removeAttribute('srcset');
    }
    if (nextState.src && image.getAttribute('src') !== nextState.src) {
      image.setAttribute('src', nextState.src);
    }
    if (image.getAttribute('alt') !== (nextState.alt || '')) {
      image.setAttribute('alt', nextState.alt || '');
    }
    if (caption && caption.textContent !== (nextState.caption || '')) {
      caption.textContent = nextState.caption || '';
    }
    return nextState !== defaultState;
  }

  function initializeProofLightbox(root) {
    var dialog = root.querySelector('[data-fp-proof-dialog]');
    if (!dialog || dialog.dataset.fpProofInitialized === 'true') return;

    var image = dialog.querySelector('[data-fp-proof-dialog-image]');
    var title = dialog.querySelector('[data-fp-proof-dialog-title]');
    var closeButton = dialog.querySelector('[data-fp-proof-close]');
    var activeTrigger = null;
    if (!image || !title || !closeButton) return;

    dialog.dataset.fpProofInitialized = 'true';

    function closeDialog() {
      if (typeof dialog.close === 'function') dialog.close();
      else {
        dialog.removeAttribute('open');
        if (activeTrigger) activeTrigger.focus();
      }
    }

    root.querySelectorAll('[data-fp-proof-open]').forEach(function (trigger) {
      trigger.addEventListener('click', function () {
        activeTrigger = trigger;
        image.src = trigger.dataset.fpProofSrc || '';
        image.alt = trigger.dataset.fpProofAlt || '';
        title.textContent = trigger.dataset.fpProofTitle || '';

        if (typeof dialog.showModal === 'function') dialog.showModal();
        else dialog.setAttribute('open', '');
        closeButton.focus();
      });
    });

    closeButton.addEventListener('click', closeDialog);
    dialog.addEventListener('click', function (event) {
      if (event.target === dialog) closeDialog();
    });
    dialog.addEventListener('close', function () {
      if (activeTrigger) activeTrigger.focus();
    });
  }

  function initialize(root) {
    if (!root || root.dataset.fpInitialized === 'true') return;
    root.dataset.fpInitialized = 'true';
    document.documentElement.classList.add('floral-purse-product');
    initializeProofLightbox(root);

    var form = root.querySelector('.fp-commission__form');
    var carryDate = root.querySelector('[data-fp-carry-date]');
    var dateHelp = root.querySelector('[data-fp-date-help]');
    var dateError = root.querySelector('[data-fp-date-error]');
    var progressText = root.querySelector('[data-fp-progress-text]');
    var progressBar = root.querySelector('[data-fp-progress-bar]');
    var submit = root.querySelector('[data-fp-submit]');
    var sms = root.querySelector('[data-fp-sms]');
    var errorBox = root.querySelector('[data-fp-errors]');
    var defaultHeroState = captureHeroState(root);
    var timeZone = root.dataset.timeZone || DEFAULT_TIME_ZONE;
    var priceLabel = root.dataset.priceLabel || '$300';

    if (!form || !carryDate || !submit) return;

    // Custom validation gives one coherent error summary. Without JavaScript,
    // the native required attributes still prevent an incomplete submission.
    form.noValidate = true;

    function refreshMinimum(now) {
      var minimum = minimumCarryDate(now || new Date());
      carryDate.min = formatForDateTimeInput(minimum, timeZone);
      if (dateHelp) {
        dateHelp.textContent = 'Earliest available carry time from this browser session: ' +
          displayCarryDate(carryDate.min, timeZone) + '. At least 24 hours\' notice is required.';
      }
    }

    function state(now) {
      var base = checkedValue(form, 'properties[Purse base]');
      var paletteField = form.querySelector('[name="properties[Palette / inspiration]"]');
      var palette = paletteField ? paletteField.value.trim() : '';
      var dateValue = carryDate.value;
      var dateValid = noticeIsValid(dateValue, now, timeZone);
      var delivery = checkedValue(form, 'properties[Delivery recipient]');

      return {
        base: base,
        palette: palette,
        dateValue: dateValue,
        dateValid: dateValid,
        delivery: delivery,
        complete: [Boolean(base), Boolean(palette), dateValid, Boolean(delivery)]
      };
    }

    function update(showDateError) {
      var now = new Date();
      var current = state(now);
      var count = current.complete.filter(Boolean).length;
      var carryDisplay = current.dateValid ? displayCarryDate(current.dateValue, timeZone) : '';

      setSummary(root, 'base', current.base, 'To choose');
      setSummary(root, 'palette', current.palette, 'To describe');
      setSummary(root, 'date', carryDisplay, 'To choose');
      setSummary(root, 'delivery', current.delivery, 'To choose');
      updateBaseHero(root, current.base, defaultHeroState);

      if (progressText) {
        progressText.textContent = count + ' of 4 ' + (count === 1 ? 'detail' : 'details') + ' complete';
      }
      if (progressBar) progressBar.style.width = (count * 25) + '%';

      if (!current.dateValue) {
        carryDate.setCustomValidity('Choose the date and time you will carry the purse.');
        carryDate.removeAttribute('aria-invalid');
        if (dateError) dateError.hidden = true;
      } else if (!current.dateValid) {
        carryDate.setCustomValidity('Choose a carry time at least 24 hours from now.');
        if (showDateError) {
          carryDate.setAttribute('aria-invalid', 'true');
          if (dateError) {
            dateError.textContent = 'Choose a carry time at least 24 hours from now in Los Angeles.';
            dateError.hidden = false;
          }
        }
      } else {
        carryDate.setCustomValidity('');
        carryDate.removeAttribute('aria-invalid');
        if (dateError) dateError.hidden = true;
      }

      if (!submit.disabled) {
        submit.textContent = count === 4
          ? 'Add configured purse — ' + priceLabel
          : 'Complete your commission — ' + priceLabel;
      }

      if (sms) {
        var message = ['Hi Bloom Boom — I\'d like help with a Designer Floral Purse.'];
        if (current.base) message.push('Base: ' + current.base + '.');
        if (current.palette) message.push('Palette/inspiration: ' + current.palette + '.');
        if (carryDisplay) message.push('Carry time: ' + carryDisplay + '.');
        sms.href = 'sms:' + root.dataset.smsNumber + '?&body=' + encodeURIComponent(message.join(' '));
      }

      return current;
    }

    function validateAll() {
      refreshMinimum(new Date());
      var current = update(true);
      var errors = [];
      var paletteField = form.querySelector('[name="properties[Palette / inspiration]"]');
      var inspirationLink = form.querySelector('[name="properties[Inspiration link]"]');

      if (!current.base) {
        errors.push({ field: form.querySelector('[name="properties[Purse base]"]'), message: 'Choose a white, red, or pink purse base.' });
      }
      if (!current.palette) {
        errors.push({ field: paletteField, message: 'Describe the palette, outfit, or inspiration.' });
      }
      if (!current.dateValue) {
        errors.push({ field: carryDate, message: 'Choose the carry date and time.' });
      } else if (!current.dateValid) {
        errors.push({ field: carryDate, message: 'Choose a Los Angeles carry time at least 24 hours from now.' });
      }
      if (!current.delivery) {
        errors.push({ field: form.querySelector('[name="properties[Delivery recipient]"]'), message: 'Choose who should receive pickup or delivery updates.' });
      }
      if (inspirationLink && inspirationLink.value && !inspirationLink.checkValidity()) {
        errors.push({ field: inspirationLink, message: 'Enter the inspiration link as a complete URL, beginning with https://.' });
      }

      setErrorBox(errorBox, errors);
      return errors;
    }

    form.addEventListener('input', function (event) {
      if (event.target === carryDate) refreshMinimum(new Date());
      setErrorBox(errorBox, []);
      update(false);
    });

    form.addEventListener('change', function () {
      setErrorBox(errorBox, []);
      update(false);
    });

    form.addEventListener('submit', function (event) {
      var errors = validateAll();
      if (!errors.length) return;

      event.preventDefault();
      event.stopPropagation();
      if (errorBox) errorBox.focus({ preventScroll: true });
      if (errors[0].field) errors[0].field.focus();
    });

    global.addEventListener('pageshow', function () {
      refreshMinimum(new Date());
      update(false);
    });

    refreshMinimum(new Date());
    update(false);
  }

  function initializeAll() {
    document.querySelectorAll('[data-fp-commission]').forEach(initialize);
  }

  var api = {
    MINIMUM_NOTICE_MS: MINIMUM_NOTICE_MS,
    formatForDateTimeInput: formatForDateTimeInput,
    zonedDateTimeToDate: zonedDateTimeToDate,
    minimumCarryDate: minimumCarryDate,
    noticeIsValid: noticeIsValid,
    captureHeroState: captureHeroState,
    updateBaseHero: updateBaseHero,
    initialize: initialize
  };

  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  global.FloralPurseCommission = api;

  if (typeof document !== 'undefined') {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', initializeAll, { once: true });
    } else {
      initializeAll();
    }
  }
})(typeof window !== 'undefined' ? window : globalThis);
